import type { EdgeImageResponse } from '../index.edge'
import { FigmaComplexTemplate, FigmaImageResponseProps } from '../types'

type InternalFigmaImageResponseProps = FigmaImageResponseProps & {
  Response: EdgeImageResponse
}

export const FigmaImageResponse = async ({
  url,
  template,
  fonts,
  imageResponseOptions,
  Response,
}: InternalFigmaImageResponseProps) => {
  const { fileId, nodeId } = parseFigmaUrl(url)

  const figmaAPIToken = assertValue(
    process.env.FIGMA_PERSONAL_ACCESS_TOKEN,
    'Missing environment variable: `FIGMA_PERSONAL_ACCESS_TOKEN`. You can get one at https://www.figma.com/developers/api#authentication'
  )

  const figmaResponse = await fetch(
    `https://api.figma.com/v1/images/${fileId}?ids=${nodeId}&svg_outline_text=false&format=svg&svg_include_id=true`,
    {
      method: 'GET',
      headers: {
        'X-FIGMA-TOKEN': figmaAPIToken,
      },
      cache: 'no-store',
    }
  )
  // Returns the URL for the SVG
  const figmaResponseJson = await figmaResponse.json()
  const svgDownloadPath = figmaResponseJson.images[nodeId.replace('-', ':')]

  // Fetches the SVG's contents
  const svgResponse = await fetch(svgDownloadPath, { cache: 'no-store' })
  const svg = await svgResponse.text()
  const { width, height } = getSvgDimensions(svg)
  const textNodes = getTextNodes(svg)
  const textNodeAttributes = textNodes.map(parseSvgText)

  return new Response(
    {
      key: '0',
      type: 'div',
      props: {
        style: { display: 'flex' },
        children: [
          {
            type: 'img',
            props: {
              style: { position: 'absolute' },
              alt: '',
              width,
              height,
              src: svgToBase64(svg),
            },
          },
          {
            type: 'div',
            props: {
              style: { display: 'flex', position: 'relative', width: '100%' },
              children: textNodeAttributes.map((textNode) => {
                const t = template[textNode.id]

                let value = ''
                if (t === undefined) {
                  value = textNode.content
                } else if (isComplexTemplate(t)) {
                  value = t.value
                } else {
                  value = t
                }

                let cssProps: React.CSSProperties = {}
                let centerHorizontally = false

                if (isComplexTemplate(t) && t.props) {
                  const {
                    centerHorizontally: centerHorizontallyProp,
                    ...otherCSSProps
                  } = t.props
                  cssProps = otherCSSProps
                  centerHorizontally = centerHorizontallyProp || false
                }

                if (centerHorizontally) {
                  const templateStyles: React.CSSProperties = {
                    color: textNode.fill,
                    marginTop: `${
                      parseInt(textNode.y) - parseInt(textNode.fontSize)
                    }px`,
                    fontWeight: textNode.fontWeight || '400',
                    fontSize: textNode.fontSize,
                    fontFamily: textNode.fontFamily,
                    letterSpacing: textNode.letterSpacing,
                    textAlign: 'center',
                    ...cssProps,
                  }

                  return {
                    type: 'div',
                    props: {
                      style: {
                        display: 'flex',
                        justifyContent: 'center',
                        position: 'absolute',
                        width: '100%',
                      },
                      children: {
                        type: 'div',
                        props: {
                          style: templateStyles,
                          children: value,
                        },
                      },
                    },
                  }
                }

                return {
                  type: 'div',
                  props: {
                    style: {
                      position: 'absolute',
                      color: textNode.fill,
                      left: `${textNode.x}px`,
                      top: `${
                        parseInt(textNode.y) - parseInt(textNode.fontSize)
                      }px`,
                      fontWeight: textNode.fontWeight || '400',
                      fontSize: textNode.fontSize,
                      fontFamily: textNode.fontFamily,
                      letterSpacing: textNode.letterSpacing,
                      ...cssProps,
                    },
                    children: value,
                  },
                }
              }),
            },
          },
        ],
      },
    },
    {
      width,
      height,
      fonts,
      ...imageResponseOptions,
    }
  )
}

const isComplexTemplate = (
  template: string | FigmaComplexTemplate
): template is FigmaComplexTemplate => {
  return (
    typeof template !== 'string' &&
    template !== undefined &&
    'value' in template
  )
}

function svgToBase64(svg: string): string {
  // Encode the SVG string to base64
  const base64 = Buffer.from(svg).toString('base64')

  // Return the base64 encoded string
  return 'data:image/svg+xml;base64,' + base64
}

function getSvgDimensions(svg: string): { width: number; height: number } {
  const widthMatch = svg.match(/width="(\d+)/)
  const heightMatch = svg.match(/height="(\d+)/)

  if (widthMatch && heightMatch) {
    const width = parseInt(widthMatch[1], 10)
    const height = parseInt(heightMatch[1], 10)

    return { width, height }
  }

  return { width: 0, height: 0 }
}

function getTextNodes(svg: string): string[] {
  const regex = /<text[^>]*>(.*?)<\/text>/g
  let match
  const matches = []

  while ((match = regex.exec(svg)) !== null) {
    matches.push(match[0])
  }

  return matches
}

function parseSvgText(svgText: string): {
  id: string
  fill: string
  fontFamily: string
  fontSize: string
  fontWeight: string
  letterSpacing: string
  x: string
  y: string
  content: string
} {
  const id = svgText.match(/id="([^"]*)"/)?.[1] || ''
  const fill = svgText.match(/fill="([^"]*)"/)?.[1] || ''
  const fontFamily = svgText.match(/font-family="([^"]*)"/)?.[1] || ''
  const fontSize = svgText.match(/font-size="([^"]*)"/)?.[1] || ''
  const fontWeight = svgText.match(/font-weight="([^"]*)"/)?.[1] || ''
  const letterSpacing = svgText.match(/letter-spacing="([^"]*)"/)?.[1] || ''
  const x = svgText.match(/<tspan[^>]*x="([^"]*)"/)?.[1] || ''
  const y = svgText.match(/<tspan[^>]*y="([^"]*)"/)?.[1] || ''
  const content = svgText.match(/<tspan[^>]*>([^<]*)<\/tspan>/)?.[1] || ''

  return {
    id,
    fill,
    fontFamily,
    fontSize,
    fontWeight,
    letterSpacing,
    x,
    y,
    content,
  }
}

function parseFigmaUrl(figmaUrl: string): { fileId: string; nodeId: string } {
  const regex = /\/file\/([^/]+)\/[^?]+\?[^#]*node-id=([^&#]+)/
  const match = figmaUrl.match(regex)

  let fileId = ''
  let nodeId = ''

  if (match) {
    fileId = match[1] || ''
    nodeId = match[2] || ''
  }

  return { fileId, nodeId }
}

function assertValue<T>(v: T | undefined, errorMessage: string): T {
  if (v === undefined) {
    throw new Error(errorMessage)
  }

  return v
}
